import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

// Types for global app state
interface Notification {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
  duration?: number; // Auto-dismiss after ms (default: 5000)
}

interface AppState {
  // Notification system
  notifications: Notification[];
  
  // Modal states
  isSettingsModalOpen: boolean;
  isHelpModalOpen: boolean;
  
  // App preferences (persisted)
  theme: 'light' | 'dark' | 'system';
  soundEnabled: boolean;
  animationsEnabled: boolean;
  
  // Performance preferences
  reducedMotion: boolean;
  
  // Actions
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  
  openSettingsModal: () => void;
  closeSettingsModal: () => void;
  
  openHelpModal: () => void;
  closeHelpModal: () => void;
  
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setSoundEnabled: (enabled: boolean) => void;
  setAnimationsEnabled: (enabled: boolean) => void;
  setReducedMotion: (reduced: boolean) => void;
}

/**
 * Global app state store with persistence for user preferences
 * Handles notifications, modals, and app-wide settings
 */
export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        notifications: [],
        isSettingsModalOpen: false,
        isHelpModalOpen: false,
        theme: 'system',
        soundEnabled: true,
        animationsEnabled: true,
        reducedMotion: false,

        // Notification actions
        addNotification: (notification) => {
          const id = Math.random().toString(36).substr(2, 9);
          const newNotification = { ...notification, id };
          
          set((state) => ({
            notifications: [...state.notifications, newNotification]
          }), false, 'addNotification');

          // Auto-dismiss after duration (default 5 seconds)
          const duration = notification.duration ?? 5000;
          if (duration > 0) {
            setTimeout(() => {
              // Use the store instance directly to avoid stale closures
              const currentState = get();
              if (currentState.notifications.some(n => n.id === id)) {
                currentState.removeNotification(id);
              }
            }, duration);
          }
        },

        removeNotification: (id) =>
          set((state) => ({
            notifications: state.notifications.filter(n => n.id !== id)
          }), false, 'removeNotification'),

        clearAllNotifications: () =>
          set({ notifications: [] }, false, 'clearAllNotifications'),

        // Modal actions
        openSettingsModal: () =>
          set({ isSettingsModalOpen: true }, false, 'openSettingsModal'),

        closeSettingsModal: () =>
          set({ isSettingsModalOpen: false }, false, 'closeSettingsModal'),

        openHelpModal: () =>
          set({ isHelpModalOpen: true }, false, 'openHelpModal'),

        closeHelpModal: () =>
          set({ isHelpModalOpen: false }, false, 'closeHelpModal'),

        // Preference actions
        setTheme: (theme) =>
          set({ theme }, false, 'setTheme'),

        setSoundEnabled: (enabled) =>
          set({ soundEnabled: enabled }, false, 'setSoundEnabled'),

        setAnimationsEnabled: (enabled) =>
          set({ animationsEnabled: enabled }, false, 'setAnimationsEnabled'),

        setReducedMotion: (reduced) =>
          set({ reducedMotion: reduced }, false, 'setReducedMotion'),
      }),
      {
        name: 'app-store', // localStorage key
        // Only persist user preferences, not ephemeral state
        partialize: (state) => ({
          theme: state.theme,
          soundEnabled: state.soundEnabled,
          animationsEnabled: state.animationsEnabled,
          reducedMotion: state.reducedMotion,
        }),
      }
    ),
    {
      name: 'app-store', // DevTools name
    }
  )
);

const bindSelector = <Result>(
  selector: (state: AppState) => Result
) => () => useAppStore(selector);

const pick = <Key extends keyof AppState>(key: Key) => (state: AppState) => state[key];

export const useNotifications = bindSelector((state) => state.notifications);
export const useTheme = bindSelector(pick('theme'));
export const useSoundEnabled = bindSelector(pick('soundEnabled'));
export const useAnimationsEnabled = bindSelector(pick('animationsEnabled'));
export const useReducedMotion = bindSelector(pick('reducedMotion'));

export const useAppPreferences = () => useAppStore(
  useShallow((state) => ({
    soundEnabled: state.soundEnabled,
    animationsEnabled: state.animationsEnabled,
    reducedMotion: state.reducedMotion,
  }))
);

export const useSettingsModalOpen = bindSelector(pick('isSettingsModalOpen'));
export const useHelpModalOpen = bindSelector(pick('isHelpModalOpen'));

export const useAddNotification = bindSelector(pick('addNotification'));
export const useRemoveNotification = bindSelector(pick('removeNotification'));
export const useClearNotifications = bindSelector(pick('clearAllNotifications'));

export const useNotificationActions = () => useAppStore(
  useShallow((state) => ({
    add: state.addNotification,
    remove: state.removeNotification,
    clear: state.clearAllNotifications,
  }))
);

export const useOpenSettingsModal = bindSelector(pick('openSettingsModal'));
export const useCloseSettingsModal = bindSelector(pick('closeSettingsModal'));

export const useOpenHelpModal = bindSelector(pick('openHelpModal'));
export const useCloseHelpModal = bindSelector(pick('closeHelpModal'));

export const useSetTheme = bindSelector(pick('setTheme'));
export const useSetSoundEnabled = bindSelector(pick('setSoundEnabled'));
export const useSetAnimationsEnabled = bindSelector(pick('setAnimationsEnabled'));
export const useSetReducedMotion = bindSelector(pick('setReducedMotion'));